// src/components/logistique/parametrage/ReglagesBus.jsx
// ============================================================================
// Paramétrage de la logistique - les bus scolaires
// ============================================================================
//
// Un circuit plein (signalé ou refusé), le téléphone du parent sur la feuille
// de route, la photo sur la carte de bus et le format d'impression des cartes.
// Régime A de PARAMETRAGE-MODULES.md. Écrire demande
// LOGISTIQUE.TRANSPORT.MANAGE ; sans elle, la section se lit.
//
// Les PRIX du bus (un seul sens, ticket, zones de trajet) restent dans
// Paramétrage de la scolarité : ils sont du socle - une école sans la licence
// Logistique facture le bus (décision de Daniel du 03/11/2026).
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Bus } from 'lucide-react';
import PorteurNotification from '../../ui/PorteurNotification';
import ListeDeroulante from '../../ui/ListeDeroulante';
import EnTeteSection from '../../parametrage/EnTeteSection';
import CarteReglages from '../../parametrage/CarteReglages';
import ChargementSection from '../../parametrage/ChargementSection';
import { Encadre, Interrupteur, LigneReglage } from '../../parametrage/PiecesReglage';
import { bus } from '../bus/apiBus';
import { BandeauEchec } from '../elementsLogistique';

const ACCENT = '#7c3aed';

const ReglagesBus = () => {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [enCours, setEnCours] = useState(null);
  const [toast, setToast] = useState(null);

  const charger = useCallback(async () => {
    try {
      setDonnees(await bus.parametrage());
      setErreur(null);
    } catch (e) {
      setErreur(e.message);
    }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  if (erreur && !donnees) {
    return (
      <BandeauEchec onReessayer={charger}>{erreur}</BandeauEchec>
    );
  }
  if (!donnees) return <ChargementSection accent={ACCENT} libelle="Chargement des réglages des bus…" />;
  const gerer = Boolean(donnees.droits?.gerer);
  const r = donnees.reglages;

  const changer = async (cle, valeur) => {
    const avant = r[cle];
    setDonnees((d) => ({ ...d, reglages: { ...d.reglages, [cle]: valeur } }));
    setEnCours(cle);
    try {
      const reglages = await bus.enregistrerParametrage({ [cle]: valeur });
      setDonnees((d) => ({ ...d, reglages }));
      setToast({ message: 'Configuration mise à jour', type: 'success' });
    } catch (e) {
      setDonnees((d) => ({ ...d, reglages: { ...d.reglages, [cle]: avant } }));
      setToast({ message: e.message, type: 'error' });
    } finally {
      setEnCours(null);
    }
  };

  return (
    <div className="space-y-5">
      <PorteurNotification notification={toast} onFermer={() => setToast(null)} />
      <EnTeteSection
        icone={Bus}
        titre="Les bus scolaires"
        sousTitre={gerer ? 'Circuits pleins, feuille de route du convoyeur, cartes de bus' : 'Lecture seule : il faut la permission de gérer les bus pour changer ces réglages'}
        accent={ACCENT}
        accentSombre="#5b21b6"
      />
      <CarteReglages titre="Circuits" accent={ACCENT} dense>
        <LigneReglage
          accent={ACCENT}
          libelle="Un circuit plein"
          description="Quand un circuit a plus d'élèves que de places dans son bus : le signaler (l'affectation passe, l'alerte s'affiche) ou refuser l'affectation de trop."
        >
          <div className="w-64">
            <ListeDeroulante
              aria-label="Un circuit plein"
              valeur={r.bus_circuit_plein}
              desactive={!gerer || enCours === 'bus_circuit_plein'}
              onChange={(v) => changer('bus_circuit_plein', v)}
              options={[{ valeur: 'signaler', libelle: 'Signaler, sans bloquer' }, { valeur: 'refuser', libelle: "Refuser l'affectation" }]}
            />
          </div>
        </LigneReglage>
      </CarteReglages>
      <CarteReglages titre="Feuille de route" accent={ACCENT} dense>
        <LigneReglage
          actif={r.bus_feuille_telephone}
          accent={ACCENT}
          libelle="Téléphone du parent"
          description="Le numéro d'un parent de chaque élève sur la feuille de route du convoyeur, pour l'appeler à l'arrêt. Éteint, la colonne disparaît."
        >
          <Interrupteur actif={r.bus_feuille_telephone} accent={ACCENT} libelle="Téléphone du parent" desactive={!gerer} enCours={enCours === 'bus_feuille_telephone'} onBasculer={() => changer('bus_feuille_telephone', !r.bus_feuille_telephone)} />
        </LigneReglage>
      </CarteReglages>
      <CarteReglages titre="Cartes de bus" accent={ACCENT} dense>
        <LigneReglage
          actif={r.bus_carte_photo}
          accent={ACCENT}
          libelle="Photo sur la carte"
          description="La photo de l'élève sur sa carte de bus. Éteinte, la carte porte le nom, la classe, le circuit, les arrêts et le QR."
        >
          <Interrupteur actif={r.bus_carte_photo} accent={ACCENT} libelle="Photo sur la carte" desactive={!gerer} enCours={enCours === 'bus_carte_photo'} onBasculer={() => changer('bus_carte_photo', !r.bus_carte_photo)} />
        </LigneReglage>
        <LigneReglage accent={ACCENT} libelle="Impression proposée" description="Le format choisi d'avance dans la fenêtre d'impression des cartes ; on peut toujours en changer au moment d'imprimer.">
          <div className="w-64">
            <ListeDeroulante
              aria-label="Impression des cartes"
              valeur={r.bus_carte_format}
              desactive={!gerer || enCours === 'bus_carte_format'}
              onChange={(v) => changer('bus_carte_format', v)}
              options={[{ valeur: '10', libelle: 'Planche A4 de 10 cartes' }, { valeur: '1', libelle: 'Une carte par page (imprimante PVC)' }]}
            />
          </div>
        </LigneReglage>
      </CarteReglages>
      <Encadre titre="Et les prix du bus ?">
        Le prix d'un seul sens, celui du ticket et les zones de trajet se règlent dans Paramétrage de la scolarité :
        ils font partie de ce que l'école facture, et une école sans la licence Logistique doit pouvoir facturer le bus.
      </Encadre>
    </div>
  );
};

export default ReglagesBus;
